import React, { useCallback } from 'react';
import {
  makeStyles,
  Dialog,
  DialogContent,
  Box,
  Button,
  DialogTitle,
  Grid,
} from '@material-ui/core';
import H6 from '../../../../common/typographys/H6';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Body2 from '../../../../common/typographys/Body2';
import H5 from '../../../../common/typographys/H5';
import { ReactComponent as VoteFavor } from '../../../../../assets/images/dialogVoteFavor.svg';
import { ReactComponent as VoteAgainst } from '../../../../../assets/images/dialogVoteAgainst.svg';
import { ReactComponent as VoteAbstention } from '../../../../../assets/images/dialogVoteAbstention.svg';
import { ReactComponent as NoVote } from '../../../../../assets/images/dialogNoVote.svg';

const useStyles = makeStyles(theme => ({
  content: {
    padding: '50px 60px',
  },
  paddingTitle: {
    padding: '40px 60px 55px 60px',
  },
  boxButton: {
    marginTop: '50px',
  },
  buttonCancel: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  buttonConfirm: {
    color: theme.palette.neutrals[1],
    backgroundColor: theme.palette.primary.A400,
    '&:hover': {
      color: theme.palette.neutrals[1],
      backgroundColor: theme.palette.primary.A700,
    },
  },
  voteItemCommon: {
    height: '124px',
    backgroundColor: theme.palette.primary[50],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary[100],
    },
  },
  selectedBox: {
    backgroundColor: theme.palette.primary[200],
  },
  favor: {
    '& *': {
      fill: `${theme.palette.colorsPalette.TrueGreen}!important`,
    },
  },
  against: {
    '& *': {
      fill: `${theme.palette.error.main}!important`,
    },
  },
  abstention: {
    '& *': {
      fill: `${theme.palette.colorsPalette.Orange}!important`,
    },
  },
  novote: {
    '& *': {
      fill: `${theme.palette.colorsPalette.TrueBlue}!important`,
    },
  },
  voteFavor: {
    transition: 'fill 0.2s ease',
    '&:hover': {
      cursor: 'pointer',
      '& *': {
        fill: `${theme.palette.colorsPalette.TrueGreen}!important`,
      },
    },
  },
  voteAgainst: {
    transition: 'fill 0.2s ease',
    '&:hover': {
      cursor: 'pointer',
      '& *': {
        fill: `${theme.palette.error.main}!important`,
      },
    },
  },
  voteAbstention: {
    transition: 'fill 0.2s ease',
    '&:hover': {
      cursor: 'pointer',
      '& *': {
        fill: `${theme.palette.colorsPalette.Orange}!important`,
      },
    },
  },
  voteNoVote: {
    transition: 'fill 0.2s ease',
    '&:hover': {
      cursor: 'pointer',
      '& *': {
        fill: `${theme.palette.colorsPalette.TrueBlue}!important`,
      },
    },
  },
  common: {
    color: theme.palette.neutrals[3],
    backgroundColor: theme.palette.neutrals[1],
    borderRadius: '50%',
    width: '50px',
    height: '50px',
  },
  whiteColor: {
    color: `${theme.palette.neutrals[1]}!important`,
  },
  selectedVote: {
    backgroundColor: theme.palette.primary.A200,
  },
  spacingBoxes: {
    marginLeft: '10px',
    marginRight: '10px',
  },
  marginLabel: {
    marginBottom: '6px',
  },
  marginTheme: {
    marginBottom: '1px',
  },
  voteDescription: {
    marginBottom: '6px',
  },
}));

const DialogSecretVote = ({
  open,
  proposal,
  handleSubmit,
  setVote,
  vote,
  isVoteBlocked,
}) => {
  const classes = useStyles();

  const updateVote = useCallback(
    value => e => {
      e.preventDefault();

      setVote(value);
    },
    [setVote]
  );

  return (
    <Dialog maxWidth="md" open={open} fullWidth disableBackdropClick disableEscapeKeyDown>
      <DialogTitle className={classes.paddingTitle} disableTypography>
        <Body2 secondary className={classes.marginLabel}>
          Proposta em votação
        </Body2>
        <H5>{proposal.name || ' '}</H5>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {!isVoteBlocked ? (
          <>
            <Grid container spacing={1}>
              <Grid item lg={3} xs={6}>
                <H6 className={classes.voteDescription} center>A favor</H6>
                <Box
                  className={clsx(classes.voteItemCommon, classes.voteFavor, {
                    [classes.selectedBox]: vote === 'favor',
                  })}
                  onClick={updateVote('favor')}
                >
                  <VoteFavor className={clsx({ [classes.favor]: vote === 'favor' })} />
                </Box>
              </Grid>
              <Grid item lg={3} xs={6}>
                <H6 className={classes.voteDescription} center>Contra</H6>
                <Box
                  className={clsx(classes.voteItemCommon, classes.voteAgainst, {
                    [classes.selectedBox]: vote === 'against',
                  })}
                  onClick={updateVote('against')}
                >
                  <VoteAgainst className={clsx({ [classes.against]: vote === 'against' })} />
                </Box>
              </Grid>
              <Grid item lg={3} xs={6}>
                <H6 className={classes.voteDescription} center>Abstenção</H6>
                <Box
                  className={clsx(classes.voteItemCommon, classes.voteAbstention, {
                    [classes.selectedBox]: vote === 'abstention',
                  })}
                  onClick={updateVote('abstention')}
                >
                  <VoteAbstention className={clsx({ [classes.abstention]: vote === 'abstention' })} />
                </Box>
              </Grid>
              <Grid item lg={3} xs={6}>
                <H6 className={classes.voteDescription} center>Escusa</H6>
                <Box
                  className={clsx(classes.voteItemCommon, classes.voteNoVote, {
                    [classes.selectedBox]: vote === 'novote',
                  })}
                  onClick={updateVote('novote')}
                >
                  <NoVote className={clsx({ [classes.novote]: vote === 'novote' })} />
                </Box>
              </Grid>
            </Grid>

            <Box className={classes.boxButton}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={vote === ''}
                className={classes.buttonConfirm}
              >
                Confirmar voto
              </Button>
            </Box>
          </>
        ) : (
          <H6>O seu voto está bloqueado para esta proposta.</H6>
        )}
      </DialogContent>
    </Dialog>
  );
};

DialogSecretVote.defaultProps = {
  vote: '',
  isVoteBlocked: false,
};

DialogSecretVote.propTypes = {
  open: PropTypes.bool.isRequired,
  proposal: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setVote: PropTypes.func.isRequired,
  vote: PropTypes.string,
  isVoteBlocked: PropTypes.bool,
};

export default DialogSecretVote;
